import { StrictMode, useEffect, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { observer } from 'mobx-react';

import {
    createMarketingContext,
    StandaloneOpenFeatureProvider,
} from '@servicetitan/standalone-feature-flags';
import { Frame, Page, Spinner, Stack } from '@servicetitan/design-system';
import { HashBrowserRouter } from '@servicetitan/hash-browser-router';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { initAjaxHandlers } from '@servicetitan/ajax-handlers';
import { Notifications, NotificationsService } from '@servicetitan/notifications';
import { BrandApi, OnboardingApi } from '@servicetitan/marketing-services-api/settings';
import { PartitionApi } from '@servicetitan/marketing-services-api/dist/settings/partitions.mrk.api';
import { MarketingRouteConfigurationProvider } from '@servicetitan/marketing-global-route';
import { AuthType, WithAuthentication } from '@servicetitan/standalone-ui';
import { StandaloneRootProvider, StandaloneRootStore } from '@servicetitan/standalone-root';

import { AppRoutes } from './modules/common/components/routes/routes';
import { AppStore } from './modules/common/stores/app.store';
import { Header } from './modules/common/components/header/header';
import { AppNavigation } from './modules/common/components/navigation/navigation';
import { DefaultBrandStore } from './modules/settings/main-settings/stores/default-brand.store';
import { routeConfiguration, Routes, SAFE_REDIRECT_PATH } from './modules/common/enums/routes';
import { IfOnboarded } from './modules/settings/common/components/onboarding/onboarding';
import { SafeRedirect } from './modules/common/components/safe-redirect/safe-redirect';
import { Features } from './modules/features/components/features';
import { SettingsPage } from './modules/settings/common/components/settings-page';

import 'expose-loader?exposes=backendless|backendless!backendless';
import './app.css';

const AppUnwrapped = provide({
    singletons: [OnboardingApi, DefaultBrandStore, BrandApi, AppStore, PartitionApi],
})(
    observer(() => {
        const [notifications, appStore, { tenant, appSettings }] = useDependencies(
            NotificationsService,
            AppStore,
            StandaloneRootStore,
        );

        useEffect(() => {
            initAjaxHandlers({
                notifier: {
                    show: options => {
                        if (options.detail?.isConnectionError) {
                            return;
                        }
                        notifications.show(options);
                    },
                },
            });
        }, [notifications]);

        const evaluationContext = useMemo(
            () => createMarketingContext(tenant.parentId!, tenant.externalId),
            [tenant.externalId, tenant.parentId],
        );

        if (appStore.isLoading) {
            return (
                <Frame>
                    <Stack
                        className="w-100"
                        alignItems="center"
                        justifyContent="center"
                        direction="row"
                    >
                        <Spinner size="large" />
                    </Stack>
                </Frame>
            );
        }

        return (
            <StandaloneOpenFeatureProvider
                context={evaluationContext}
                sdkKey={appSettings.featureFlagClientId}
            >
                <Features>
                    <Frame header={<Header />}>
                        <Switch>
                            <Route exact path={SAFE_REDIRECT_PATH}>
                                <SafeRedirect />
                            </Route>
                            <Route path={Routes.SettingsSection}>
                                <SettingsPage />
                            </Route>
                            <IfOnboarded>
                                <Route path={Routes.Home}>
                                    <Page
                                        sidebar={<AppNavigation />}
                                        maxWidth="wide"
                                        className="page-top-level"
                                    >
                                        <Switch>
                                            <Redirect
                                                exact
                                                from={Routes.Home}
                                                to={Routes.OneTimeCampaigns}
                                            />
                                            <AppRoutes />
                                        </Switch>
                                    </Page>
                                </Route>
                            </IfOnboarded>
                        </Switch>
                    </Frame>
                </Features>
            </StandaloneOpenFeatureProvider>
        );
    }),
);

export const App = () => {
    return (
        <StrictMode>
            <HashBrowserRouter>
                <Notifications>
                    <MarketingRouteConfigurationProvider value={routeConfiguration}>
                        <WithAuthentication type={AuthType.SIP}>
                            <StandaloneRootProvider>
                                <AppUnwrapped />
                            </StandaloneRootProvider>
                        </WithAuthentication>
                    </MarketingRouteConfigurationProvider>
                </Notifications>
            </HashBrowserRouter>
        </StrictMode>
    );
};
